import React, { useEffect } from 'react';
import { OnboardingStep, OnboardingFlow } from '../../../contexts/OnboardingContext';
import { 
  CheckCircleIcon, 
  SparklesIcon, 
  RocketLaunchIcon,
  ArrowRightIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { useOnboarding } from '../../../contexts/OnboardingContext';

interface CompletionStepProps {
  step: OnboardingStep;
  flow: OnboardingFlow;
}

export function CompletionStep({ step, flow }: CompletionStepProps) {
  const { progress } = useOnboarding();

  // Confetti effect on mount
  useEffect(() => {
    // Simple confetti animation
    const createConfetti = () => {
      const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
      const confettiCount = 50;
      
      for (let i = 0; i < confettiCount; i++) {
        const confetti = document.createElement('div');
        confetti.style.cssText = `
          position: fixed;
          width: 10px;
          height: 10px;
          background: ${colors[Math.floor(Math.random() * colors.length)]};
          top: -10px;
          left: ${Math.random() * 100}vw;
          z-index: 9999;
          border-radius: 50%;
          pointer-events: none;
          animation: confettiFall ${3 + Math.random() * 2}s linear forwards;
        `;
        
        document.body.appendChild(confetti);
        
        setTimeout(() => {
          confetti.remove();
        }, 5000);
      }
    };

    // Add confetti animation CSS
    if (!document.getElementById('confetti-styles')) {
      const style = document.createElement('style');
      style.id = 'confetti-styles';
      style.textContent = `
        @keyframes confettiFall {
          0% {
            transform: translateY(-10px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    createConfetti();
  }, []);

  const getCompletionStats = () => {
    if (!progress) return { completed: 0, skipped: 0, total: 0 };
    
    return {
      completed: progress.completedSteps.length,
      skipped: progress.skippedSteps.length,
      total: flow.steps.length
    };
  };

  const stats = getCompletionStats();
  const completionRate = Math.round((stats.completed / stats.total) * 100);

  const getNextSteps = () => {
    if (flow.id === 'first_time_user') {
      return [
        {
          icon: RocketLaunchIcon,
          title: 'Connect More Apps',
          description: 'Add Slack, GitHub, or any of 2,500+ available integrations',
          action: 'Explore Apps'
        },
        {
          icon: SparklesIcon,
          title: 'Plan Your Workflow',
          description: 'Use the visual planner to design your integration architecture',
          action: 'Open Planner'
        },
        {
          icon: CheckCircleIcon,
          title: 'Generate Config',
          description: 'Create MCP configuration files for Claude Desktop and other clients',
          action: 'Generate Config'
        }
      ];
    } else if (flow.id === 'developer_flow') {
      return [
        {
          icon: RocketLaunchIcon,
          title: 'Test Your Setup',
          description: 'Try making API calls to your connected services',
          action: 'Test APIs'
        },
        {
          icon: SparklesIcon,
          title: 'View Documentation',
          description: 'Learn about advanced features and customization options',
          action: 'Read Docs'
        },
        {
          icon: CheckCircleIcon,
          title: 'Deploy to Production',
          description: 'Set up your production environment and deploy your configs',
          action: 'Deploy'
        }
      ];
    }

    return [];
  };

  const nextSteps = getNextSteps();

  return (
    <div className="text-center">
      {/* Success icon with animation */}
      <div className="relative mx-auto flex h-20 w-20 items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-green-100 dark:bg-green-900 animate-ping"></div>
        <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
          <CheckCircleIcon className="h-10 w-10 text-green-600 dark:text-green-400" />
        </div>
      </div>

      {/* Completion message */}
      <h3 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">
        🎉 Congratulations!
      </h3>
      
      <p className="mt-2 text-lg text-gray-600 dark:text-gray-300">
        You've completed the {flow.name}!
      </p>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {stats.completed}
          </div>
          <div className="text-sm text-green-700 dark:text-green-300">
            Completed
          </div>
        </div>

        {stats.skipped > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {stats.skipped}
            </div>
            <div className="text-sm text-yellow-700 dark:text-yellow-300">
              Skipped
            </div>
          </div>
        )}

        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
            {completionRate}%
          </div>
          <div className="text-sm text-indigo-700 dark:text-indigo-300">
            Complete
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="mt-6 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
        <div className="flex items-center justify-center space-x-2 mb-2">
          <StarIcon className="h-5 w-5 text-yellow-500" />
          <span className="font-medium text-gray-900 dark:text-white">Achievement Unlocked!</span>
          <StarIcon className="h-5 w-5 text-yellow-500" />
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {flow.id === 'first_time_user' 
            ? "Pipedream MCP Explorer - You've taken your first steps into the world of seamless integrations!"
            : "MCP Developer - You're ready to build powerful integrations with Pipedream MCP!"
          }
        </p>
      </div>

      {/* Next steps */}
      {nextSteps.length > 0 && (
        <div className="mt-8">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            What's Next?
          </h4>
          
          <div className="space-y-3">
            {nextSteps.map((nextStep, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                onClick={() => {
                  // In a real app, these would navigate to the appropriate sections
                  console.log(`Navigate to: ${nextStep.action}`);
                }}
              >
                <div className="flex items-center space-x-3">
                  <nextStep.icon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  <div className="text-left">
                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                      {nextStep.title}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {nextStep.description}
                    </div>
                  </div>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feedback */}
      <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          How was your onboarding experience?
        </p>
        <div className="flex items-center justify-center space-x-2">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => {
                // Track rating
                console.log(`User rated onboarding: ${rating}/5`);
              }}
            >
              <StarIcon 
                className="h-6 w-6 text-gray-300 hover:text-yellow-500 transition-colors"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Fun fact */}
      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          🚀 Fun fact: You now have access to more integrations than most enterprise platforms!
        </p>
      </div>
    </div>
  );
}